import { useTranslation } from 'react-i18next';
import type { EffectiveCreditLimitsView } from '../../api.js';
import { toCredits } from '../../components/AllocationPanel.js';
import { formatResetAt } from '../../utils/reset-at.js';
import type { LimitSubjectKind, LimitSubjectOption } from './CreditLimitForm.js';

export function EffectiveLimitsPanel({
  preview,
  subjectKind,
  subjectId,
  subjects,
  models,
  catalogModelId,
  locale,
  busy,
  onSubjectKindChange,
  onSubjectIdChange,
  onModelChange
}: {
  preview?: EffectiveCreditLimitsView;
  subjectKind: LimitSubjectKind;
  subjectId: string;
  subjects: LimitSubjectOption[];
  models: LimitSubjectOption[];
  catalogModelId: string;
  locale: string;
  busy: boolean;
  onSubjectKindChange: (kind: LimitSubjectKind) => void;
  onSubjectIdChange: (id: string) => void;
  onModelChange: (id: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="panel effective-limits">
      <h2>{t('creditLimits.effectiveTitle')}</h2>
      {preview?.enforcementMode && (
        <p>{t('creditEnforcement.label')}: {t(`creditEnforcement.${preview.enforcementMode}`)}</p>
      )}
      {!!preview?.unguardedFreeTierModelIds?.length && (
        <p role="status">
          {t('creditEnforcement.unguarded', { models: preview.unguardedFreeTierModelIds.join(', ') })}
        </p>
      )}
      <label>
        <span>{t('creditLimits.subject')}</span>
        <select
          value={subjectKind}
          onChange={(event) => onSubjectKindChange(event.target.value as LimitSubjectKind)}
          disabled={busy}
        >
          <option value="user">{t('creditLimits.subjectUser')}</option>
          <option value="group">{t('creditLimits.subjectGroup')}</option>
        </select>
      </label>
      <label>
        <span>{t('creditLimits.subjectTarget')}</span>
        <select
          value={subjectId}
          onChange={(event) => onSubjectIdChange(event.target.value)}
          disabled={busy || subjects.length === 0}
        >
          {subjects.length === 0 ? (
            <option value="">{t('creditLimits.noSubjects')}</option>
          ) : (
            subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.label}
              </option>
            ))
          )}
        </select>
      </label>
      <label>
        <span>{t('creditLimits.model')}</span>
        <select
          value={catalogModelId}
          onChange={(event) => onModelChange(event.target.value)}
          disabled={busy}
        >
          <option value="">{t('creditLimits.modelAll')}</option>
          {models.map((model) => (
            <option key={model.id} value={model.id}>
              {model.label}
            </option>
          ))}
        </select>
      </label>

      {preview ? (
        <div aria-live="polite">
          {/* Funding is money, limits are ceilings. They are rendered as two
              separate blocks so they can never read as one running total. */}
          <h3>{t('creditLimits.effectiveFunding')}</h3>
          <p>
            {preview.funded.unlimited
              ? t('creditLimits.summaryUnlimited', {
                  consumed: toCredits(preview.funded.consumed)
                })
              : t('creditLimits.summary', {
                  granted: toCredits(preview.funded.granted ?? 0),
                  consumed: toCredits(preview.funded.consumed),
                  remaining: toCredits(preview.funded.remaining ?? 0)
                })}
          </p>

          <h3>{t('creditLimits.effectiveLimits')}</h3>
          {preview.limits.length === 0 ? (
            <p className="muted">{t('creditLimits.effectiveEmpty')}</p>
          ) : (
            <ul className="effective-limit-list">
              {preview.limits.map((entry) => (
                <li key={`${entry.origin}:${entry.id}`}>
                  <span>
                    {t(`creditLimits.origin_${entry.origin}`)} ·{' '}
                    {t(`creditLimits.cycle_${entry.cycle}`)} ·{' '}
                    {entry.catalogModelId ?? t('creditLimits.modelAll')}
                  </span>{' '}
                  <span>
                    {t('creditLimits.effectiveNumbers', {
                      limit: toCredits(entry.limitMillis),
                      consumed: toCredits(entry.consumedMillis),
                      remaining: toCredits(entry.remainingMillis)
                    })}
                  </span>{' '}
                  {t(`creditEnforcement.${entry.overageBehavior}`)}{' '}
                  {entry.binding ? <strong>★ {t('creditLimits.binding')}</strong> : null}{' '}
                  {entry.cycle === 'lifetime'
                    ? null
                    : (formatResetAt(entry.resetAt, locale) ?? null)}
                </li>
              ))}
            </ul>
          )}
          <p className="muted">{t('creditLimits.noSumNote')}</p>
        </div>
      ) : null}
    </section>
  );
}
